import { Navigate, type RouteObject } from 'react-router';
import { AppShell } from '@/shell/AppShell';
import { TimerPage } from '@/features/timer/TimerPage';
import { Pending } from '@/shell/Pending';
import { RouteError } from '@/shell/ErrorBoundary';
import { PageViews } from '@/shell/PageViews';

const lazy = (load: () => Promise<Record<string, unknown>>, name: string): RouteObject['lazy'] =>
  async () => ({ Component: (await load())[name] as RouteObject['Component'] });

/**
 * The Timer is bundled with the shell because it is the first thing anyone opens; every other screen is fetched
 * on demand (and precached by the service worker, so it is instant offline too).
 */
export const routes: RouteObject[] = [{
  // One pathless parent, so a crash on any screen lands on our crash page rather than the router's developer page,
  // and so every screen reports its page view from one place.
  element: <PageViews />,
  errorElement: <RouteError />,
  children: [
  { path: '/auth', HydrateFallback: Pending, lazy: lazy(() => import('@/features/auth/AuthPage'), 'AuthPage') },
  { path: '/auth/complete', HydrateFallback: Pending, lazy: lazy(() => import('@/features/auth/AuthPage'), 'AuthCompletePage') },
  { path: '/join/:token', HydrateFallback: Pending, lazy: lazy(() => import('@/features/team/JoinPage'), 'JoinPage') },
  // `/` is the public page. Asking for it is a choice, so it is never redirected away; the app lives at /timer.
  { path: '/', HydrateFallback: Pending, lazy: lazy(() => import('@/features/landing/LandingPage'), 'LandingPage') },
  {
    element: <AppShell />,
    HydrateFallback: Pending,
    children: [
      { path: 'timer', element: <TimerPage /> },
      { path: 'sessions', HydrateFallback: Pending, lazy: lazy(() => import('@/features/sessions/SessionsPage'), 'SessionsPage') },
      { path: 'projects/:projectId?', HydrateFallback: Pending, lazy: lazy(() => import('@/features/projects/ProjectsPage'), 'ProjectsPage') },
      { path: 'team/upgrade', HydrateFallback: Pending, lazy: lazy(() => import('@/features/team/UpgradePage'), 'UpgradePage') },
      { path: 'team/:sub?', HydrateFallback: Pending, lazy: lazy(() => import('@/features/team/TeamPage'), 'TeamPage') },
      { path: 'settings/:section?', HydrateFallback: Pending, lazy: lazy(() => import('@/features/settings/SettingsPage'), 'SettingsPage') },
      { path: '*', element: <Navigate to="/timer" replace /> },
    ],
  },
],
}];
